import { ChangeDetectionStrategy, Component, computed, signal, ViewEncapsulation } from '@angular/core';
import { ActivityEntityName, getLookupTargetEntity, MetadataEntityFieldData, MetadataEntityFieldType } from '@jms/model';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { getManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { WbValueComponent } from '@workbuddy/ui/v2/components/primitives/value';
import { BaseCellRenderer } from './base.cell.renderer';

export interface ValueCellParams extends ICellRendererParams {
  field: MetadataEntityFieldData;
}

@Component({
  selector: 'value-cell',
  standalone: true,
  imports: [WbValueComponent],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `value-cell { display: flex; align-items: center; height: 100%; width: 100%; min-width: 0; overflow: hidden; } value-cell.value-cell-numeric { justify-content: flex-end; } value-cell.value-cell-numeric > wb-value.wb-value-numeric, value-cell.value-cell-numeric > wb-value.wb-value-numeric > .wb-content { flex: 0 1 auto; text-align: right; }`,
    // Address text owns its wrapping styles, so constrain it explicitly in fixed-height board cells.
    `value-cell wb-value.wb-value-truncate > wb-value-address .address-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }`,
    `value-cell a.value-cell-link { color: var(--wb-color-link, #2563eb); text-decoration: none; display: inline-flex; align-items: center; min-width: 0; max-width: 100%; overflow: hidden; } value-cell a.value-cell-link wb-value { color: inherit; } value-cell a.value-cell-link:hover wb-value { text-decoration: underline; }`,
  ],
  host: { '[class.value-cell-numeric]': '$numeric()' },
  // All board-table values render as text (or text + swatch/avatar); ellipsis when the fixed column width clips.
  // A Lookup whose target has a record page in this domain links there; the click stops at the anchor so
  // opening the record never also previews or selects the row.
  template: `
    @if ($href(); as href) {
      <a class="value-cell-link" [href]="href" target="_blank" rel="noopener noreferrer" (click)="$event.stopPropagation()">
        <wb-value [field]="$field()" [data]="$data()" [value]="$value()" [truncate]="true" />
      </a>
    } @else {
      <wb-value [field]="$field()" [data]="$data()" [value]="$value()" [truncate]="true" />
    }
  `,
})
export class ValueCellRenderer extends BaseCellRenderer<any, ValueCellParams> {

  protected $field = signal<MetadataEntityFieldData | null>(null);
  protected $data = signal<any>(null);
  protected $value = signal<any>(null);
  protected $href = signal<string | null>(null);
  protected $numeric = computed(() => {
    const type = this.$field()?.type;
    const scalar = Array.isArray(type) ? type[0] : type;
    return scalar === MetadataEntityFieldType.Number || scalar === MetadataEntityFieldType.Currency || scalar === MetadataEntityFieldType.Percentage;
  });

  public displayLogic(params: ValueCellParams): void {

    this.$field.set(params.field ?? null);
    this.$data.set(params.data);
    // AG Grid resolves the value from the colDef `field` (the fully-qualified column path, which
    // matches the row shape). Pass it explicitly so wb-value renders it directly instead of
    // re-deriving from field.path — custom fields are keyed by a bare path (customFields.<id>) that
    // doesn't match the queue row's nested 'entity.customFields.<id>' shape.
    this.$value.set(params.value ?? null);
    this.$href.set(this.lookupRecordHref(params.field, params.value));
  }

  /** The referenced record's page from the TARGET entity's manifest; null (plain text) for multi lookups or a target with no record page here, e.g. the Client Portal. */
  private lookupRecordHref(field: MetadataEntityFieldData | undefined, value: any): string | null {

    if (field?.type !== MetadataEntityFieldType.Lookup) return null;
    const id = value?._id;
    if (typeof id !== 'string' || !id) return null;
    const target = getLookupTargetEntity(field) as ActivityEntityName | null;
    if (!target) return null;

    return getManifest(target)?.routes?.record?.(id) ?? null;
  }
}
